import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output
} from "@angular/core";
import { BPInputModel } from "@modules/core/components/bp-input/bp-input.component";
import { BehaviorSubject, combineLatest, fromEvent, Observable } from "rxjs";
import { GooglePlaceAddress } from "@models/google_place_address";
import { GOOGLE_SEARCH_DEBOUNCE_TIME } from "@helpers/constants";
import { debounceTime, map } from "rxjs/operators";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ProfileWithValidateable } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { GooglePlacesService } from "@modules/shared/bp-google-autocomplete/bp-google-autocomplete.facade";
import { google } from "google-maps";

@UntilDestroy()
@Component({
	selector: "bp-google-autocomplete",
	templateUrl: "./bp-google-autocomplete.component.html",
	styleUrls: ["./bp-google-autocomplete.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class BpGoogleAutocompleteComponent implements OnInit, AfterViewInit {
	@Input() public googleCountryRestriction: string[] = [];
	@Input() public showManualAddressEdit: boolean = true;
	@Input() public label: string = "";
	@Input() public bottomLabel: string = "";
	@Input() public placeholder: string = "";
	@Input() public model: BPInputModel = { clearError: () => undefined, hasError: () => false, wiggle: () => "" };
	@Input() public profile: ProfileWithValidateable;
	@Output() public onSuggestionsUpdate: EventEmitter<GooglePlaceAddress> = new EventEmitter<GooglePlaceAddress>();
	@Output() public onManualAddress: EventEmitter<void> = new EventEmitter<void>();

	public defaultPreSelectedStreet: string = "";
	public hasStreetPreSelected: boolean = false;
	public inputIgnored: boolean = true;
	public showSuggestions: boolean = false;
	public hasInput: boolean = false;

	public value: string = "";
	public inputGroup: ElementRef = null;
	public hasStreetNumber: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	private sessionToken: google.maps.places.AutocompleteSessionToken = null;
	public predictions: google.maps.places.AutocompletePrediction[] = [];

	public addressState$: Observable<AddressState> = combineLatest([
		this.googleAutoFillService.searchFinished.asObservable(),
		this.hasStreetNumber.asObservable(),
		this.googleAutoFillService.predictionResultsSubject.asObservable()
	]).pipe(
		map(([searchFinished, hasStreetNumber, predictionResults]) => {
			let filteredResults = predictionResults.filter((pr) => pr.getCity() && pr.getStreet() && pr.getPostCode());

			if (this.profile) {
				filteredResults = this.sortedAddresses(filteredResults, this.profile.country_name);
			}

			return {
				searchFinished,
				hasStreetNumber,
				predictionResults: filteredResults
			};
		})
	);

	constructor(private googleAutoFillService: GooglePlacesService) {}

	public ngOnInit(): void {
		if (this.profile?.street !== "") {
			this.defaultPreSelectedStreet = this.profile?.street;
			this.hasStreetPreSelected = true;
		}
	}

	public ngAfterViewInit(): void {
		fromEvent(this.inputGroup.nativeElement, "keyup")
			.pipe(debounceTime(GOOGLE_SEARCH_DEBOUNCE_TIME), untilDestroyed(this))
			.subscribe(async () => {
				this.showSuggestions = true;
				this.sessionToken = await this.googleAutoFillService.getSessionToken(this.sessionToken);
				const predictions = await this.googleAutoFillService.getPlacePredictions(
					this.inputGroup.nativeElement.value,
					this.googleCountryRestriction,
					this.sessionToken
				);
				this.predictions = predictions;
				this.hasInput = this.inputGroup.nativeElement.value.length > 0;
			});
	}

	public updateHasInput(event): void {
		this.hasInput = event.length > 0;
	}

	public async searchResultClick(
		prediction: google.maps.places.AutocompletePrediction,
		event: Event,
		state: AddressState
	): Promise<void> {
		this.sessionToken = await this.googleAutoFillService.getSessionToken(this.sessionToken);
		const address = await this.googleAutoFillService.getPlaceDetails(prediction.place_id, this.sessionToken);
		this.hasStreetNumber.next(!!address.getStreetNumber());
		this.defaultPreSelectedStreet = address.getStreet();
		this.showSuggestions = false;
		this.inputIgnored = false;
		this.onSuggestionsUpdate.emit(address);
		this.sessionToken = null; // Reset session token after selection
		if (!address.getStreetNumber() || address.getStreetNumber() === "") {
			event.preventDefault();
		}
		state.searchFinished = true;
		this.inputGroup.nativeElement.value = this.defaultPreSelectedStreet;
	}

	public handleFocusOut(state: AddressState): void {
		this.showSuggestions = false;
		if (this.inputIgnored && this.hasInput && !this.hasStreetPreSelected) {
			this.profile.street = "";
			this.inputGroup.nativeElement.value = "";
		} else {
			this.profile.street = this.defaultPreSelectedStreet;
			this.inputGroup.nativeElement.value = this.defaultPreSelectedStreet;
		}
		// Reset the flag for the next interaction
		state.searchFinished = true;
		this.inputIgnored = true;
		this.sessionToken = null; // Reset session token on blur
	}

	public onManualAddressClick(): void {
		this.inputIgnored = false;
		this.onManualAddress.emit();
	}

	public sortedAddresses(addresses: GooglePlaceAddress[], countryName: string): GooglePlaceAddress[] {
		return addresses.sort((address1, address2) => {
			const isAddress1CountryMatch = address1.getCountry() === countryName;
			const isAddress2CountryMatch = address2.getCountry() === countryName;

			if (isAddress1CountryMatch && !isAddress2CountryMatch) {
				return -1; // Address1 comes first
			} else if (!isAddress1CountryMatch && isAddress2CountryMatch) {
				return 1; // Address2 comes first
			}
			return 0; // No change in order
		});
	}
}

export interface AddressState {
	searchFinished: boolean;
	hasStreetNumber: boolean;
	predictionResults: GooglePlaceAddress[];
}
